import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Building2,
  MoreHorizontal,
  Edit,
  Trash2,
  Globe,
  Phone,
  Mail,
  Zap,
  Flame,
  ChevronDown,
  ChevronUp,
  Package,
  Euro,
  ToggleLeft,
  ToggleRight,
  Upload,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Ban,
  Search,
  Tv,
  Wifi,
  FileSpreadsheet,
  Download,
  Image
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOLOGIE_OFFERTA = [
  { value: 'luce', label: 'Luce' },
  { value: 'gas', label: 'Gas' },
  { value: 'dual', label: 'Dual' },
  { value: 'internet', label: 'Internet/Fibra' },
  { value: 'tv', label: 'TV' },
  { value: 'tv_internet', label: 'TV + Internet' },
];

const PACCHETTI_TV = [
  { value: 'intrattenimento', label: 'Intrattenimento' },
  { value: 'cinema', label: 'Cinema' },
  { value: 'sport', label: 'Sport' },
  { value: 'calcio', label: 'Calcio' },
  { value: 'kids', label: 'Kids' },
];

const SERVIZI_EXTRA = [
  { value: 'netflix', label: 'Netflix' },
  { value: 'paramount', label: 'Paramount+' },
  { value: 'disney', label: 'Disney+' },
  { value: 'sky_go', label: 'Sky Go' },
  { value: 'sky_stream', label: 'Sky Stream' },
  { value: 'ultra_hd', label: '4K / Ultra HD' },
  { value: 'wifi_sicuro', label: 'WiFi Sicuro' },
  { value: 'modem_incluso', label: 'Modem incluso' },
];

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    nome: '', codice: '', telefono: '', email: '', sito_web: '', note: '', attiva: true
  });
  const [offerForm, setOfferForm] = useState({
    nome: '', tipologia: 'luce', prezzo_fisso: '', prezzo_variabile: '',
    costo_energia_kwh: '', quota_fissa_mese: '',
    costo_energia_kwh_gas: '', quota_fissa_mese_gas: '',
    descrizione: '', commissione_agente: '', data_inizio: '', data_fine: '', attiva: true,
    // Internet / TV fields
    tecnologia: '', velocita_download: '', velocita_upload: '',
    prezzo_mensile: '', prezzo_dopo_promo: '', costo_attivazione: '',
    modem_incluso: false, chiamate_illimitate: false,
    pacchetti_tv: [], servizi_extra: [], prezzo_promo: '', durata_promo_mesi: '', canali_principali: '',
    tipologia_cliente: ''
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingArera, setSyncingArera] = useState(false);
  const [syncingSegugio, setSyncingSegugio] = useState(false);
  const [syncingCS, setSyncingCS] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [offerAlerts, setOfferAlerts] = useState(null);
  const csvInputRef = React.useRef(null);
  const logoInputRef = React.useRef(null);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);

  // Filters & bulk selection
  const [offerFilters, setOfferFilters] = useState({ tipologia: 'all', stato: 'all', search: '', tipologia_cliente: 'all' });
  const [companyFilter, setCompanyFilter] = useState('attive'); // 'attive' | 'disattive' | 'all'
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const filterOffers = (offers) => {
    if (!offers) return [];
    const q = (offerFilters.search || '').trim().toLowerCase();
    return offers.filter(o => {
      if (offerFilters.tipologia !== 'all' && o.tipologia !== offerFilters.tipologia) return false;
      if (offerFilters.stato === 'attiva' && !o.attiva) return false;
      if (offerFilters.stato === 'disattiva' && o.attiva) return false;
      if (offerFilters.tipologia_cliente === 'residenziale' && o.tipologia_cliente === 'business') return false;
      if (offerFilters.tipologia_cliente === 'business' && o.tipologia_cliente !== 'business') return false;
      if (q) {
        const haystack = [o.nome, o.descrizione, o.fornitore_originale_cte, o.codice_offerta_cte]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  };

  const toggleOfferSelection = (offerId) => {
    setSelectedOfferIds(prev => prev.includes(offerId) ? prev.filter(id => id !== offerId) : [...prev, offerId]);
  };

  const selectAllFiltered = (offers) => {
    const filtered = filterOffers(offers);
    const filteredIds = filtered.map(o => o.id);
    const allSelected = filteredIds.every(id => selectedOfferIds.includes(id));
    setSelectedOfferIds(prev => allSelected ? prev.filter(id => !filteredIds.includes(id)) : [...new Set([...prev, ...filteredIds])]);
  };

  const handleBulkToggle = async (attiva) => {
    if (selectedOfferIds.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/offers/bulk-toggle-attiva`, { offer_ids: selectedOfferIds, attiva });
      toast.success(res.data.message);
      setSelectedOfferIds([]);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore azione massiva');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOfferIds.length === 0) return;
    if (!window.confirm(`Vuoi eliminare DEFINITIVAMENTE ${selectedOfferIds.length} offerte? L'operazione non e' reversibile.`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/offers/bulk-delete`, { offer_ids: selectedOfferIds });
      toast.success(res.data.message);
      setSelectedOfferIds([]);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione massiva');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleToggleAttiva = async (company) => {
    try {
      const res = await axios.post(`${API_URL}/api/companies/${company.id}/toggle-attiva`);
      toast.success(res.data.message);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  const { hasRole } = useAuth();

  useEffect(() => { fetchCompanies(); fetchOfferAlerts(); }, []);

  const fetchOfferAlerts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/offers/alerts`);
      setOfferAlerts(res.data);
    } catch (e) {}
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle compagnie');
    } finally {
      setLoading(false);
    }
  };

  const resetCompanyForm = () => setCompanyForm({
    nome: '', codice: '', telefono: '', email: '', sito_web: '', note: '', attiva: true
  });

  const resetOfferForm = () => setOfferForm({
    nome: '', tipologia: 'luce', prezzo_fisso: '', prezzo_variabile: '',
    costo_energia_kwh: '', quota_fissa_mese: '',
    costo_energia_kwh_gas: '', quota_fissa_mese_gas: '',
    descrizione: '', commissione_agente: '', data_inizio: '', data_fine: '', attiva: true,
    tecnologia: '', velocita_download: '', velocita_upload: '',
    prezzo_mensile: '', prezzo_dopo_promo: '', costo_attivazione: '',
    modem_incluso: false, chiamate_illimitate: false,
    pacchetti_tv: [], servizi_extra: [], prezzo_promo: '', durata_promo_mesi: '', canali_principali: '',
    tipologia_cliente: ''
  });

  const handleSaveCompany = async () => {
    try {
      if (editingCompany) {
        await axios.put(`${API_URL}/api/companies/${editingCompany.id}`, companyForm);
        toast.success('Compagnia aggiornata');
      } else {
        await axios.post(`${API_URL}/api/companies`, companyForm);
        toast.success('Compagnia creata');
      }
      setShowCompanyModal(false);
      setEditingCompany(null);
      resetCompanyForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Eliminare questa compagnia e tutte le sue offerte?')) return;
    try {
      await axios.delete(`${API_URL}/api/companies/${companyId}`);
      toast.success('Compagnia eliminata');
      fetchCompanies();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleUploadLogo = (companyId) => {
    setUploadingLogoFor(companyId);
    logoInputRef.current?.click();
  };

  const handleLogoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadingLogoFor) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo troppo grande (max 3MB)');
      setUploadingLogoFor(null);
      return;
    }
    const form = new FormData();
    form.append('file', file);
    const companyId = uploadingLogoFor;
    setUploadingLogoFor(null);
    try {
      await axios.post(`${API_URL}/api/companies/${companyId}/upload-logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo caricato');
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento logo');
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      nome: company.nome, codice: company.codice || '', telefono: company.telefono || '',
      email: company.email || '', sito_web: company.sito_web || '', note: company.note || '',
      attiva: company.attiva
    });
    setShowCompanyModal(true);
  };

  const handleSaveOffer = async () => {
    try {
      const payload = {
        ...offerForm,
        prezzo_fisso: offerForm.prezzo_fisso ? parseFloat(offerForm.prezzo_fisso) : null,
        costo_energia_kwh: offerForm.costo_energia_kwh ? parseFloat(offerForm.costo_energia_kwh) : null,
        quota_fissa_mese: offerForm.quota_fissa_mese ? parseFloat(offerForm.quota_fissa_mese) : null,
        costo_energia_kwh_gas: offerForm.costo_energia_kwh_gas ? parseFloat(offerForm.costo_energia_kwh_gas) : null,
        quota_fissa_mese_gas: offerForm.quota_fissa_mese_gas ? parseFloat(offerForm.quota_fissa_mese_gas) : null,
        commissione_agente: offerForm.commissione_agente ? parseFloat(offerForm.commissione_agente) : null,
        velocita_download: offerForm.velocita_download ? parseInt(offerForm.velocita_download) : null,
        velocita_upload: offerForm.velocita_upload ? parseInt(offerForm.velocita_upload) : null,
        prezzo_mensile: offerForm.prezzo_mensile ? parseFloat(offerForm.prezzo_mensile) : null,
        prezzo_dopo_promo: offerForm.prezzo_dopo_promo ? parseFloat(offerForm.prezzo_dopo_promo) : null,
        costo_attivazione: offerForm.costo_attivazione ? parseFloat(offerForm.costo_attivazione) : null,
        prezzo_promo: offerForm.prezzo_promo ? parseFloat(offerForm.prezzo_promo) : null,
        durata_promo_mesi: offerForm.durata_promo_mesi ? parseInt(offerForm.durata_promo_mesi) : null,
        pacchetti_tv: offerForm.pacchetti_tv || [],
        servizi_extra: offerForm.servizi_extra || [],
        canali_principali: offerForm.canali_principali || null,
        tipologia_cliente: offerForm.tipologia_cliente || null,
        compagnia_id: selectedCompanyId
      };
      if (editingOffer) {
        const { compagnia_id, ...updatePayload } = payload;
        await axios.put(`${API_URL}/api/offers/${editingOffer.id}`, updatePayload);
        toast.success('Offerta aggiornata');
      } else {
        await axios.post(`${API_URL}/api/offers`, payload);
        toast.success('Offerta creata');
      }
      setShowOfferModal(false);
      setEditingOffer(null);
      resetOfferForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Eliminare questa offerta?')) return;
    try {
      await axios.delete(`${API_URL}/api/offers/${offerId}`);
      toast.success('Offerta eliminata');
      fetchCompanies();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleEditOffer = (offer, companyId) => {
    setEditingOffer(offer);
    setSelectedCompanyId(companyId);
    // Reset first, then overlay - garantisce che ogni campo abbia un valore predefinito
    // e che il form Internet/TV NON compaia per offerte Luce/Gas.
    setOfferForm({
      nome: offer.nome || '',
      tipologia: offer.tipologia || 'luce',
      prezzo_fisso: offer.prezzo_fisso ?? '',
      prezzo_variabile: offer.prezzo_variabile || '',
      costo_energia_kwh: offer.costo_energia_kwh ?? '',
      quota_fissa_mese: offer.quota_fissa_mese ?? '',
      costo_energia_kwh_gas: offer.costo_energia_kwh_gas ?? '',
      quota_fissa_mese_gas: offer.quota_fissa_mese_gas ?? '',
      descrizione: offer.descrizione || '',
      commissione_agente: offer.commissione_agente ?? '',
      data_inizio: offer.data_inizio || '',
      data_fine: offer.data_fine || '',
      attiva: offer.attiva !== false,
      // Internet / TV fields
      tecnologia: offer.tecnologia || '',
      velocita_download: offer.velocita_download ?? '',
      velocita_upload: offer.velocita_upload ?? '',
      prezzo_mensile: offer.prezzo_mensile ?? '',
      prezzo_dopo_promo: offer.prezzo_dopo_promo ?? '',
      costo_attivazione: offer.costo_attivazione ?? '',
      modem_incluso: !!offer.modem_incluso,
      chiamate_illimitate: !!offer.chiamate_illimitate,
        pacchetti_tv: offer.pacchetti_tv || [],
        servizi_extra: offer.servizi_extra || [],
        prezzo_promo: offer.prezzo_promo ?? '',
        durata_promo_mesi: offer.durata_promo_mesi ?? '',
        canali_principali: offer.canali_principali || '',
        tipologia_cliente: offer.tipologia_cliente || ''
    });
    setShowOfferModal(true);
  };

  const openNewOffer = (companyId) => {
    setSelectedCompanyId(companyId);
    setEditingOffer(null);
    resetOfferForm();
    setShowOfferModal(true);
  };

  const handleUploadPdfOffer = async (e, companyId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (companyId) formData.append('compagnia_id', companyId);
      const res = await axios.post(`${API_URL}/api/offers/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`Offerta "${res.data.offerta.nome}" creata automaticamente dall'AI`);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'analisi del PDF');
    } finally {
      setUploadingPdf(false);
      e.target.value = '';
    }
  };

  const getTipologiaIcon = (tipo) => {
    if (tipo === 'luce') return <Zap className="w-4 h-4 text-amber-500" />;
    if (tipo === 'gas') return <Flame className="w-4 h-4 text-sky-500" />;
    if (tipo === 'internet') return <Globe className="w-4 h-4 text-violet-500" />;
    if (tipo === 'tv') return <Tv className="w-4 h-4 text-fuchsia-500" />;
    if (tipo === 'tv_internet') return <div className="flex"><Tv className="w-3.5 h-3.5 text-fuchsia-500" /><Wifi className="w-3.5 h-3.5 text-indigo-500 -ml-0.5" /></div>;
    return <div className="flex"><Zap className="w-3.5 h-3.5 text-amber-500" /><Flame className="w-3.5 h-3.5 text-sky-500 -ml-0.5" /></div>;
  };

  const handleSyncSpreadsheet = async () => {
    setSyncing(true);
    try {
      // First sync from Google Sheet (scadenze live)
      const sheetRes = await axios.post(`${API_URL}/api/offers/sync-sheet`);
      // Then sync from local data
      const jsonRes = await axios.post(`${API_URL}/api/offers/sync-spreadsheet`);
      toast.success(`${sheetRes.data.message}. ${jsonRes.data.message}`);
      fetchCompanies();
      fetchOfferAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSegugio = async () => {
    setSyncingSegugio(true);
    try {
      const res = await axios.post(`${API_URL}/api/offers/sync-segugio`);
      toast.success(res.data.message);
      fetchCompanies();
      fetchOfferAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync Segugio.it');
    } finally {
      setSyncingSegugio(false);
    }
  };

  const handleSyncComparaSemplice = async () => {
    setSyncingCS(true);
    try {
      const res = await axios.post(`${API_URL}/api/offers/sync-comparasemplice-live`);
      toast.success(res.data.message);
      fetchCompanies();
      fetchOfferAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync ComparaSemplice');
    } finally {
      setSyncingCS(false);
    }
  };

  const [syncingCTE, setSyncingCTE] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [importingXlsx, setImportingXlsx] = useState(false);
  const xlsxInputRef = React.useRef(null);

  const handleDownloadOffersTemplate = async (formato = 'xlsx') => {
    try {
      const res = await axios.get(`${API_URL}/api/offers/import-template?formato=${formato}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: formato === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv;charset=utf-8;'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_offerte.${formato}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template scaricato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore download template');
    }
  };

  const handleExportOffersCurrent = async (formato = 'xlsx') => {
    try {
      const res = await axios.get(`${API_URL}/api/offers/export?formato=${formato}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: formato === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv;charset=utf-8;'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.href = url;
      a.download = `offerte_export_${stamp}.${formato}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Offerte esportate. Modifica il file e ricaricalo per aggiornarle.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore export offerte');
    }
  };

  const handleImportOffersXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingXlsx(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/offers/import-bulk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        res.data.errors.slice(0, 5).forEach((err) => toast.warning(err));
      }
      fetchCompanies();
      fetchOfferAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'import');
    } finally {
      setImportingXlsx(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = '';
    }
  };
  const handleSyncCTE = async () => {
    setSyncingCTE(true);
    try {
      const res = await axios.post(`${API_URL}/api/portale-energia/sync-offerte`, {});
      toast.success(res.data.message);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync CTE');
    } finally {
      setSyncingCTE(false);
    }
  };

  const handleWipeAllOffers = async () => {
    const confirm1 = window.confirm(
      'ATTENZIONE: stai per cancellare TUTTE le offerte dal sistema.\n\n' +
      'Le compagnie NON verranno toccate, ma tutte le offerte associate saranno rimosse.\n\n' +
      'Sei sicuro di voler procedere?'
    );
    if (!confirm1) return;
    const confirm2 = window.prompt('Per confermare, digita esattamente:\nSI_CANCELLA_TUTTO');
    if (confirm2 !== 'SI_CANCELLA_TUTTO') {
      toast.error('Conferma non corretta, operazione annullata');
      return;
    }
    setWiping(true);
    try {
      const res = await axios.post(`${API_URL}/api/offers/wipe-all`, { confirm: 'SI_CANCELLA_TUTTO' });
      toast.success(res.data.message || 'Offerte cancellate');
      fetchCompanies();
      fetchOfferAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore cancellazione');
    } finally {
      setWiping(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Compagnie</h1>
          <p className="text-slate-500 mt-1">{companies.length} fornitori registrati</p>
        </div>
        {hasRole(['super_admin']) && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={xlsxInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleImportOffersXlsx}
              disabled={importingXlsx}
              data-testid="import-offers-file-input"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={importingXlsx} data-testid="import-offers-menu-btn">
                  {importingXlsx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                  {importingXlsx ? 'Importazione...' : 'Import Offerte Excel'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExportOffersCurrent('xlsx')} data-testid="export-offers-current-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Scarica offerte attuali (per modifica)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => xlsxInputRef.current?.click()} data-testid="upload-offers-xlsx">
                  <Upload className="w-4 h-4 mr-2" /> Carica file (.xlsx/.csv)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDownloadOffersTemplate('xlsx')} data-testid="download-offers-template-xlsx">
                  <Download className="w-4 h-4 mr-2" /> Template vuoto Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadOffersTemplate('csv')} data-testid="download-offers-template-csv">
                  <Download className="w-4 h-4 mr-2" /> Template vuoto CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => handleUploadPdfOffer(e, null)}
                disabled={uploadingPdf}
                data-testid="upload-pdf-offer-btn"
              />
              <Button variant="outline" asChild disabled={uploadingPdf}>
                <span>
                  {uploadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploadingPdf ? 'AI Analisi...' : 'Offerta da PDF'}
                </span>
              </Button>
            </label>
            <Button
              variant="outline"
              onClick={handleWipeAllOffers}
              disabled={wiping}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="wipe-offers-btn"
            >
              {wiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {wiping ? 'Cancello...' : 'Cancella tutte le offerte'}
            </Button>
            <Button onClick={() => { setEditingCompany(null); resetCompanyForm(); setShowCompanyModal(true); }} data-testid="create-company-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Compagnia
            </Button>
          </div>
        )}
      </div>

      {companies.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna compagnia registrata</p>
            {hasRole(['super_admin']) && (
              <Button className="mt-4" onClick={() => { resetCompanyForm(); setShowCompanyModal(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Aggiungi la prima compagnia
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Filter bar */}
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Mostra:</span>
              {[
                { v: 'attive', l: 'Solo Attive', n: companies.filter(c => c.attiva).length },
                { v: 'disattive', l: 'Disattivate', n: companies.filter(c => !c.attiva).length },
                { v: 'all', l: 'Tutte', n: companies.length },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setCompanyFilter(opt.v)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${companyFilter === opt.v ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`filter-companies-${opt.v}`}
                >
                  {opt.l} ({opt.n})
                </button>
              ))}
              <span className="text-xs text-slate-500 ml-auto">
                Le compagnie disattivate non appaiono in Comparatore e Crea Pratica, ma restano nel Listino Provvigioni.
              </span>
            </CardContent>
          </Card>

          {companies.filter(c => companyFilter === 'all' || (companyFilter === 'attive' ? c.attiva : !c.attiva)).map((company) => (
            <Card key={company.id} data-testid={`company-card-${company.id}`}>
              <CardContent className="p-0">
                <div
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)}
                >
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleUploadLogo(company.id); }}
                      className="relative group w-12 h-12 rounded-xl overflow-hidden shrink-0 focus:outline-none focus:ring-2 focus:ring-sky-500"
                      title="Clicca per caricare o cambiare il logo"
                      data-testid={`logo-upload-tap-${company.id}`}
                    >
                      {company.logo_url ? (
                        <img
                          src={company.logo_url}
                          alt={`Logo ${company.nome}`}
                          className={`w-12 h-12 rounded-xl object-contain bg-white border ${company.attiva ? 'border-sky-200' : 'border-slate-200 opacity-60'}`}
                          onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                          data-testid={`company-logo-${company.id}`}
                        />
                      ) : null}
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${company.attiva ? 'bg-sky-600' : 'bg-slate-400'}`}
                        style={{ display: company.logo_url ? 'none' : 'flex' }}
                      >
                        {company.nome[0]?.toUpperCase()}
                      </div>
                      {/* Overlay al hover: mostra icona camera */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                        <Image className="w-5 h-5 text-white" />
                      </div>
                      {/* Piccolo indicatore permanente in basso a destra: matita */}
                      <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-sky-600 text-white flex items-center justify-center border-2 border-white dark:border-slate-900">
                        <Edit className="w-2.5 h-2.5" />
                      </span>
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{company.nome}</h3>
                        {company.codice && <span className="text-xs text-slate-400 font-mono">({company.codice})</span>}
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${company.attiva ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {company.attiva ? 'Attiva' : 'Disattivata'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        {company.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{company.telefono}</span>}
                        {company.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</span>}
                        {company.sito_web && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{company.sito_web}</span>}
                        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{company.offerte?.length || 0} offerte</span>
                        {/* Offer status badges */}
                        {(() => {
                          const info = offerAlerts?.riepilogo_compagnie?.find(c => c.compagnia === company.nome);
                          if (!info) return null;
                          const updDate = info.ultimo_aggiornamento ? new Date(info.ultimo_aggiornamento) : null;
                          const daysSince = updDate ? Math.floor((new Date() - updDate) / (1000*60*60*24)) : null;
                          return (
                            <>
                              {info.offerte_scadute > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                  <AlertTriangle className="w-3 h-3" /> {info.offerte_scadute} scadute
                                </span>
                              )}
                              {info.offerte_in_scadenza_7g > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                  <AlertTriangle className="w-3 h-3" /> {info.offerte_in_scadenza_7g} in scad.
                                </span>
                              )}
                              {updDate && (
                                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${daysSince > 7 ? 'text-red-600 bg-red-50 font-medium' : daysSince > 3 ? 'text-amber-600 bg-amber-50' : 'text-slate-500'}`}>
                                  <Clock className="w-3 h-3" />
                                  agg. {updDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                                  {daysSince > 0 && ` (${daysSince}g fa)`}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {hasRole(['super_admin']) && (
                      <Button
                        variant={company.attiva ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => handleToggleAttiva(company)}
                        className={company.attiva ? 'text-red-600 border-red-200 hover:bg-red-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                        data-testid={`toggle-attiva-${company.id}`}
                      >
                        {company.attiva ? <><Ban className="w-3.5 h-3.5 mr-1" />Disattiva</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Attiva</>}
                      </Button>
                    )}
                    {hasRole(['super_admin']) && (
                      <div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditCompany(company)}>
                              <Edit className="w-4 h-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUploadLogo(company.id)} data-testid={`upload-logo-${company.id}`}>
                              <Image className="w-4 h-4 mr-2" /> Carica Logo PNG
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openNewOffer(company.id)}>
                              <Plus className="w-4 h-4 mr-2" /> Nuova Offerta
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteCompany(company.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    <div onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)} className="cursor-pointer">
                      {expandedCompany === company.id
                        ? <ChevronUp className="w-5 h-5 text-slate-400" />
                        : <ChevronDown className="w-5 h-5 text-slate-400" />
                      }
                    </div>
                  </div>
                </div>

                {expandedCompany === company.id && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50/50">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                      <h4 className="font-medium text-slate-700">Offerte Commerciali ({company.offerte?.length || 0})</h4>
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Cerca */}
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                          <Input
                            value={offerFilters.search}
                            onChange={(e) => setOfferFilters(prev => ({ ...prev, search: e.target.value }))}
                            placeholder="Cerca offerta..."
                            className="h-8 pl-7 w-40 text-xs"
                            data-testid={`search-offer-${company.id}`}
                          />
                        </div>
                        {/* Filtri */}
                        <Select value={offerFilters.tipologia} onValueChange={(v) => setOfferFilters(prev => ({ ...prev, tipologia: v }))}>
                          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Tipologia" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutte tipologie</SelectItem>
                            <SelectItem value="luce">Luce</SelectItem>
                            <SelectItem value="gas">Gas</SelectItem>
                            <SelectItem value="dual">Dual</SelectItem>
                            <SelectItem value="internet">Internet</SelectItem>
                            <SelectItem value="tv">TV</SelectItem>
                            <SelectItem value="tv_internet">TV + Internet</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={offerFilters.stato} onValueChange={(v) => setOfferFilters(prev => ({ ...prev, stato: v }))}>
                          <SelectTrigger className="h-8 w-28 text-xs"><SelectValue placeholder="Stato" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutte</SelectItem>
                            <SelectItem value="attiva">Solo attive</SelectItem>
                            <SelectItem value="disattiva">Solo inattive</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={offerFilters.tipologia_cliente} onValueChange={(v) => setOfferFilters(prev => ({ ...prev, tipologia_cliente: v }))}>
                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`filter-tipo-cliente-${company.id}`}><SelectValue placeholder="Segmento" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutti i segmenti</SelectItem>
                            <SelectItem value="residenziale">Residenziale</SelectItem>
                            <SelectItem value="business">Business (P.IVA)</SelectItem>
                          </SelectContent>
                        </Select>
                        {hasRole(['super_admin']) && (
                          <Button size="sm" variant="outline" onClick={() => openNewOffer(company.id)} data-testid={`add-offer-${company.id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Nuova
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Bulk actions bar */}
                    {hasRole(['super_admin']) && selectedOfferIds.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-sky-50 border border-sky-200 rounded-md" data-testid="bulk-actions-bar">
                        <span className="text-xs font-medium text-sky-700">{selectedOfferIds.length} selezionate</span>
                        <Button size="sm" variant="outline" onClick={() => handleBulkToggle(true)} disabled={bulkBusy} data-testid="bulk-activate-btn">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Attiva
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleBulkToggle(false)} disabled={bulkBusy} data-testid="bulk-deactivate-btn">
                          <Ban className="w-3.5 h-3.5 mr-1" /> Disattiva
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleBulkDelete} disabled={bulkBusy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-btn">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedOfferIds([])}>
                          Annulla selezione
                        </Button>
                      </div>
                    )}
                    {(!company.offerte || company.offerte.length === 0) ? (
                      <p className="text-sm text-slate-400 py-4 text-center">Nessuna offerta per questa compagnia</p>
                    ) : (() => {
                      const filtered = filterOffers(company.offerte);
                      const filteredIds = filtered.map(o => o.id);
                      const allChecked = filteredIds.length > 0 && filteredIds.every(id => selectedOfferIds.includes(id));
                      if (filtered.length === 0) {
                        return <p className="text-sm text-slate-400 py-4 text-center">Nessuna offerta corrisponde ai filtri</p>;
                      }
                      return (
                      <>
                        <p className="text-xs text-slate-500 mb-2">Visualizzate {filtered.length} di {company.offerte.length} offerte</p>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-slate-500 border-b border-slate-200">
                                {hasRole(['super_admin']) && (
                                  <th className="pb-2 pr-3 font-medium w-8">
                                    <input type="checkbox" checked={allChecked} onChange={() => selectAllFiltered(company.offerte)} className="rounded" data-testid={`select-all-${company.id}`} />
                                  </th>
                                )}
                                <th className="pb-2 pr-4 font-medium">Offerta</th>
                                <th className="pb-2 pr-4 font-medium">Tipo</th>
                                <th className="pb-2 pr-4 font-medium">Prezzo Energia</th>
                                <th className="pb-2 pr-4 font-medium">Scadenza</th>
                                <th className="pb-2 pr-4 font-medium">Stato</th>
                                {hasRole(['super_admin']) && <th className="pb-2 text-right font-medium">Azioni</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((offer) => (
                                <tr key={offer.id} className={`border-b border-slate-100 last:border-0 ${selectedOfferIds.includes(offer.id) ? 'bg-sky-50/40' : ''}`} data-testid={`offer-row-${offer.id}`}>
                                  {hasRole(['super_admin']) && (
                                    <td className="py-3 pr-3">
                                      <input type="checkbox" checked={selectedOfferIds.includes(offer.id)} onChange={() => toggleOfferSelection(offer.id)} className="rounded" data-testid={`select-offer-${offer.id}`} />
                                    </td>
                                  )}
                                  <td className="py-3 pr-4">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-medium text-slate-900">{offer.nome}</p>
                                    </div>
                                    {offer.descrizione && <p className="text-xs text-slate-400 mt-0.5 max-w-xs truncate">{offer.descrizione}</p>}
                                  </td>
                                  <td className="py-3 pr-4">
                                    <div className="flex items-center gap-1.5">
                                      {getTipologiaIcon(offer.tipologia)}
                                      <span className="capitalize">{offer.tipologia}</span>
                                    </div>
                                  </td>
                                  <td className="py-3 pr-4 tabular-nums">
                                    {offer.tipologia === 'internet' ? (
                                      <div>
                                        <span className="block font-medium">{offer.prezzo_mensile?.toFixed(2)} EUR/mese</span>
                                        {offer.prezzo_dopo_promo && offer.prezzo_dopo_promo !== offer.prezzo_mensile && (
                                          <span className="block text-[10px] text-slate-400">poi {offer.prezzo_dopo_promo.toFixed(2)}/mese</span>
                                        )}
                                        <span className="block text-[10px] text-slate-400">{offer.tecnologia} {offer.velocita_download}Mbps</span>
                                      </div>
                                    ) : (offer.tipologia === 'tv' || offer.tipologia === 'tv_internet') ? (
                                      <div>
                                        {offer.prezzo_promo != null && (
                                          <span className="block font-medium">{offer.prezzo_promo.toFixed(2)} EUR/mese</span>
                                        )}
                                        {offer.durata_promo_mesi && (
                                          <span className="block text-[10px] text-slate-400">per {offer.durata_promo_mesi} mesi</span>
                                        )}
                                        {offer.prezzo_dopo_promo && offer.prezzo_dopo_promo !== offer.prezzo_promo && (
                                          <span className="block text-[10px] text-slate-400">poi {offer.prezzo_dopo_promo.toFixed(2)}/mese</span>
                                        )}
                                        {(offer.pacchetti_tv || []).length > 0 && (
                                          <div className="flex flex-wrap gap-0.5 mt-1">
                                            {offer.pacchetti_tv.slice(0, 4).map(p => (
                                              <span key={p} className="inline-flex px-1.5 py-0 rounded text-[9px] font-medium bg-fuchsia-100 text-fuchsia-700 capitalize">{p}</span>
                                            ))}
                                            {offer.pacchetti_tv.length > 4 && <span className="text-[9px] text-slate-400">+{offer.pacchetti_tv.length - 4}</span>}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <>
                                        {offer.costo_energia_kwh && <span className="block">{offer.costo_energia_kwh.toFixed(4)} EUR/kWh</span>}
                                        {offer.costo_energia_kwh_gas && <span className="block">{offer.costo_energia_kwh_gas.toFixed(4)} EUR/Smc</span>}
                                        {offer.quota_fissa_mese > 0 && <span className="block text-[10px] text-slate-400">+{offer.quota_fissa_mese.toFixed(2)}/mese</span>}
                                        {offer.quota_fissa_mese_gas > 0 && <span className="block text-[10px] text-slate-400">+{offer.quota_fissa_mese_gas.toFixed(2)}/mese</span>}
                                        {!offer.costo_energia_kwh && !offer.costo_energia_kwh_gas && (offer.prezzo_variabile || '-')}
                                      </>
                                    )}
                                  </td>
                                  <td className="py-3 pr-4">
                                    {offer.scadenza_vendita ? (() => {
                                      const isExpired = new Date(offer.scadenza_vendita) < new Date();
                                      const isExpiringSoon = !isExpired && new Date(offer.scadenza_vendita) < new Date(Date.now() + 7 * 86400000);
                                      return (
                                        <span className={`text-xs font-medium ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : 'text-slate-500'}`}>
                                          {new Date(offer.scadenza_vendita).toLocaleDateString('it-IT')}
                                          {isExpired && ' (Scaduta)'}
                                          {isExpiringSoon && !isExpired && ' (!)'}
                                        </span>
                                      );
                                    })() : <span className="text-xs text-slate-400">-</span>}
                                  </td>
                                  <td className="py-3 pr-4">
                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${offer.attiva ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                      {offer.attiva ? 'Attiva' : 'Non Attiva'}
                                    </span>
                                  </td>
                                  {hasRole(['super_admin', 'admin', 'interno', 'back_office']) && (
                                    <td className="py-3 text-right">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-3.5 h-3.5" /></Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => handleEditOffer(offer, company.id)}>
                                            <Edit className="w-4 h-4 mr-2" /> Modifica
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem onClick={() => handleDeleteOffer(offer.id)} className="text-red-600">
                                            <Trash2 className="w-4 h-4 mr-2" /> Elimina
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobile cards */}
                        <div className="md:hidden divide-y divide-slate-100">
                          {filtered.map((offer) => (
                            <div key={offer.id} className="py-3" data-testid={`offer-card-${offer.id}`}>
                              <div className="flex items-start justify-between">
                                {hasRole(['super_admin']) && (
                                  <input type="checkbox" checked={selectedOfferIds.includes(offer.id)} onChange={() => toggleOfferSelection(offer.id)} className="rounded mr-2 mt-1" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {getTipologiaIcon(offer.tipologia)}
                                    <p className="font-medium text-slate-900 text-sm truncate">{offer.nome}</p>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                    {offer.costo_energia_kwh && <span>{offer.costo_energia_kwh.toFixed(4)} EUR/kWh</span>}
                                    {offer.costo_energia_kwh_gas && <span>{offer.costo_energia_kwh_gas.toFixed(4)} EUR/Smc</span>}
                                    {!offer.costo_energia_kwh && !offer.costo_energia_kwh_gas && <span>{offer.prezzo_variabile || '-'}</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${offer.attiva ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {offer.attiva ? 'Attiva' : 'Non Attiva'}
                                  </span>
                                </div>
                              </div>
                              {offer.scadenza_vendita && (
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Scade: {new Date(offer.scadenza_vendita).toLocaleDateString('it-IT')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Company Modal */}
      <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Modifica Compagnia' : 'Nuova Compagnia'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={companyForm.nome} onChange={(e) => setCompanyForm({...companyForm, nome: e.target.value})} placeholder="Es. Enel Energia" data-testid="company-nome" />
              </div>
              <div className="space-y-2">
                <Label>Codice</Label>
                <Input value={companyForm.codice} onChange={(e) => setCompanyForm({...companyForm, codice: e.target.value})} placeholder="ENEL" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input value={companyForm.telefono} onChange={(e) => setCompanyForm({...companyForm, telefono: e.target.value})} placeholder="+39 800 123456" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={companyForm.email} onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})} placeholder="info@enel.it" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sito Web</Label>
              <Input value={companyForm.sito_web} onChange={(e) => setCompanyForm({...companyForm, sito_web: e.target.value})} placeholder="https://www.enel.it" />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <textarea className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950" value={companyForm.note} onChange={(e) => setCompanyForm({...companyForm, note: e.target.value})} placeholder="Note aggiuntive..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompanyModal(false)}>Annulla</Button>
            <Button onClick={handleSaveCompany} disabled={!companyForm.nome} data-testid="submit-company-btn">
              {editingCompany ? 'Salva Modifiche' : 'Crea Compagnia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOffer ? 'Modifica Offerta' : 'Nuova Offerta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Offerta *</Label>
                <Input value={offerForm.nome} onChange={(e) => setOfferForm({...offerForm, nome: e.target.value})} placeholder="Es. E-Light Web" data-testid="offer-nome" />
              </div>
              <div className="space-y-2">
                <Label>Tipologia *</Label>
                <Select value={offerForm.tipologia} onValueChange={(v) => setOfferForm({...offerForm, tipologia: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOLOGIE_OFFERTA.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prezzo Fisso (EUR)</Label>
                <Input type="number" step="0.01" value={offerForm.prezzo_fisso} onChange={(e) => setOfferForm({...offerForm, prezzo_fisso: e.target.value})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Prezzo Variabile</Label>
                <Input value={offerForm.prezzo_variabile} onChange={(e) => setOfferForm({...offerForm, prezzo_variabile: e.target.value})} placeholder="PUN + spread" />
              </div>
            </div>
            {(offerForm.tipologia === 'luce' || offerForm.tipologia === 'dual') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="space-y-2">
                  <Label>Costo Energia Luce (EUR/kWh)</Label>
                  <Input type="number" step="0.0001" value={offerForm.costo_energia_kwh} onChange={(e) => setOfferForm({...offerForm, costo_energia_kwh: e.target.value})} placeholder="0.0850" />
                  <p className="text-xs text-slate-500">Prezzo per kWh (luce)</p>
                </div>
                <div className="space-y-2">
                  <Label>Quota Fissa Luce (EUR/mese)</Label>
                  <Input type="number" step="0.01" value={offerForm.quota_fissa_mese} onChange={(e) => setOfferForm({...offerForm, quota_fissa_mese: e.target.value})} placeholder="9.90" />
                  <p className="text-xs text-slate-500">Quota fissa mensile (luce)</p>
                </div>
              </div>
            )}
            {(offerForm.tipologia === 'gas' || offerForm.tipologia === 'dual') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Costo Energia Gas (EUR/Smc)</Label>
                  <Input type="number" step="0.0001" value={offerForm.costo_energia_kwh_gas} onChange={(e) => setOfferForm({...offerForm, costo_energia_kwh_gas: e.target.value})} placeholder="0.4500" />
                  <p className="text-xs text-slate-500">Prezzo per Smc (gas)</p>
                </div>
                <div className="space-y-2">
                  <Label>Quota Fissa Gas (EUR/mese)</Label>
                  <Input type="number" step="0.01" value={offerForm.quota_fissa_mese_gas} onChange={(e) => setOfferForm({...offerForm, quota_fissa_mese_gas: e.target.value})} placeholder="7.50" />
                  <p className="text-xs text-slate-500">Quota fissa mensile (gas)</p>
                </div>
              </div>
            )}
            {(offerForm.tipologia === 'tv' || offerForm.tipologia === 'tv_internet') && (
              <div className="space-y-4 bg-fuchsia-50 rounded-lg p-4 border border-fuchsia-200">
                <p className="text-sm font-semibold text-fuchsia-700 flex items-center gap-2">
                  Dettagli {offerForm.tipologia === 'tv_internet' ? 'TV + Internet' : 'TV'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prezzo Promo (EUR/mese)</Label>
                    <Input type="number" step="0.01" value={offerForm.prezzo_promo || ''} onChange={(e) => setOfferForm({...offerForm, prezzo_promo: e.target.value})} placeholder="14.99" data-testid="offer-prezzo-promo" />
                  </div>
                  <div className="space-y-2">
                    <Label>Durata Promo (mesi)</Label>
                    <Select value={offerForm.durata_promo_mesi ? String(offerForm.durata_promo_mesi) : ''} onValueChange={(v) => setOfferForm({...offerForm, durata_promo_mesi: v})}>
                      <SelectTrigger data-testid="offer-durata-promo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6">6 mesi</SelectItem>
                        <SelectItem value="12">12 mesi</SelectItem>
                        <SelectItem value="18">18 mesi</SelectItem>
                        <SelectItem value="24">24 mesi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prezzo Dopo Promo (EUR/mese)</Label>
                    <Input type="number" step="0.01" value={offerForm.prezzo_dopo_promo || ''} onChange={(e) => setOfferForm({...offerForm, prezzo_dopo_promo: e.target.value})} placeholder="24.90" />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo Attivazione (EUR)</Label>
                    <Input type="number" step="0.01" value={offerForm.costo_attivazione || ''} onChange={(e) => setOfferForm({...offerForm, costo_attivazione: e.target.value})} placeholder="0" />
                  </div>
                </div>
                {offerForm.tipologia === 'tv_internet' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Tecnologia</Label>
                      <Select value={offerForm.tecnologia || 'FTTH'} onValueChange={(v) => setOfferForm({...offerForm, tecnologia: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FTTH">FTTH</SelectItem>
                          <SelectItem value="FTTC">FTTC</SelectItem>
                          <SelectItem value="FWA">FWA</SelectItem>
                          <SelectItem value="OpenFiber">Open Fiber</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Download (Mbps)</Label>
                      <Input type="number" value={offerForm.velocita_download || ''} onChange={(e) => setOfferForm({...offerForm, velocita_download: e.target.value})} placeholder="1000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Upload (Mbps)</Label>
                      <Input type="number" value={offerForm.velocita_upload || ''} onChange={(e) => setOfferForm({...offerForm, velocita_upload: e.target.value})} placeholder="300" />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Pacchetti TV inclusi</Label>
                  <div className="flex flex-wrap gap-2" data-testid="offer-pacchetti-tv">
                    {PACCHETTI_TV.map(p => {
                      const active = (offerForm.pacchetti_tv || []).includes(p.value);
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => setOfferForm(s => ({ ...s, pacchetti_tv: active ? s.pacchetti_tv.filter(x => x !== p.value) : [...(s.pacchetti_tv || []), p.value] }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? 'bg-fuchsia-600 border-fuchsia-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:border-fuchsia-400'}`}
                          data-testid={`pacchetto-${p.value}`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Servizi extra</Label>
                  <div className="flex flex-wrap gap-2" data-testid="offer-servizi-extra">
                    {SERVIZI_EXTRA.map(s => {
                      const active = (offerForm.servizi_extra || []).includes(s.value);
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setOfferForm(prev => ({ ...prev, servizi_extra: active ? prev.servizi_extra.filter(x => x !== s.value) : [...(prev.servizi_extra || []), s.value] }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:border-indigo-400'}`}
                          data-testid={`servizio-${s.value}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Canali / contenuti principali</Label>
                  <Input value={offerForm.canali_principali || ''} onChange={(e) => setOfferForm({...offerForm, canali_principali: e.target.value})} placeholder="Serie A, Champions, Sky Original, Prime visioni..." />
                </div>
              </div>
            )}
            {offerForm.tipologia === 'internet' && (
              <div className="space-y-4 bg-violet-50 rounded-lg p-4 border border-violet-200">
                <p className="text-sm font-semibold text-violet-700">Dettagli Internet/Fibra</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tecnologia</Label>
                    <Select value={offerForm.tecnologia || 'FTTH'} onValueChange={(v) => setOfferForm({...offerForm, tecnologia: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FTTH">FTTH</SelectItem>
                        <SelectItem value="FTTC">FTTC</SelectItem>
                        <SelectItem value="FWA">FWA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Download (Mbps)</Label>
                    <Input type="number" value={offerForm.velocita_download || ''} onChange={(e) => setOfferForm({...offerForm, velocita_download: e.target.value})} placeholder="1000" />
                  </div>
                  <div className="space-y-2">
                    <Label>Upload (Mbps)</Label>
                    <Input type="number" value={offerForm.velocita_upload || ''} onChange={(e) => setOfferForm({...offerForm, velocita_upload: e.target.value})} placeholder="300" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prezzo Mensile (EUR)</Label>
                    <Input type="number" step="0.01" value={offerForm.prezzo_mensile || ''} onChange={(e) => setOfferForm({...offerForm, prezzo_mensile: e.target.value})} placeholder="19.90" />
                  </div>
                  <div className="space-y-2">
                    <Label>Prezzo Dopo Promo (EUR)</Label>
                    <Input type="number" step="0.01" value={offerForm.prezzo_dopo_promo || ''} onChange={(e) => setOfferForm({...offerForm, prezzo_dopo_promo: e.target.value})} placeholder="24.90" />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo Attivazione (EUR)</Label>
                    <Input type="number" step="0.01" value={offerForm.costo_attivazione || ''} onChange={(e) => setOfferForm({...offerForm, costo_attivazione: e.target.value})} placeholder="0" />
                  </div>
                </div>
              </div>
            )}
            {!hasRole(['interno', 'back_office']) && (
              <div className="space-y-2">
                <Label>Commissione Agente (EUR)</Label>
                <Input type="number" step="0.01" value={offerForm.commissione_agente} onChange={(e) => setOfferForm({...offerForm, commissione_agente: e.target.value})} placeholder="0.00" />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Input type="date" value={offerForm.data_inizio} onChange={(e) => setOfferForm({...offerForm, data_inizio: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Data Fine (scadenza vendita)</Label>
                <Input type="date" value={offerForm.data_fine} onChange={(e) => setOfferForm({...offerForm, data_fine: e.target.value})} data-testid="offer-data-fine-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipologia Cliente</Label>
              <Select value={offerForm.tipologia_cliente || 'none'} onValueChange={(v) => setOfferForm({...offerForm, tipologia_cliente: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="offer-tipologia-cliente-select">
                  <SelectValue placeholder="Non classificata" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non classificata</SelectItem>
                  <SelectItem value="residenziale">Residenziale (privati)</SelectItem>
                  <SelectItem value="business">Business (P.IVA)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Le offerte Business vengono mostrate nel comparatore solo quando il cliente ha una Partita IVA.</p>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <textarea className="w-full h-20 px-3 py-2 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-950" value={offerForm.descrizione} onChange={(e) => setOfferForm({...offerForm, descrizione: e.target.value})} placeholder="Descrizione dell'offerta..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)}>Annulla</Button>
            <Button onClick={handleSaveOffer} disabled={!offerForm.nome} data-testid="submit-offer-btn">
              {editingOffer ? 'Salva Modifiche' : 'Crea Offerta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden input per upload logo */}
      <input
        type="file"
        ref={logoInputRef}
        onChange={handleLogoFileSelected}
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        data-testid="logo-file-input"
      />
    </div>
  );
};

export default Companies;
